import React, {useEffect, useMemo, useState} from 'react';
import {getGenres, resetGenres, saveGenres} from '../../libraries/AdminApi';

const CATCH_ALL = '__catch_all';
const UNASSIGNED = '__unassigned';
const n = (v) => v.toLocaleString();
const slug = (label) => label.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '').slice(0, 30) || 'family';

/**
 * Genre families (partial genre matches + the genre profiles) and catch-all
 * genres (which borrow the artist's main genre). Edits are a draft until saved.
 */
export default function GenresPage() {
    const [server, setServer] = useState(null);
    const [families, setFamilies] = useState([]);
    const [catchAll, setCatchAll] = useState([]);
    const [selected, setSelected] = useState(UNASSIGNED);
    const [filter, setFilter] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const load = (r) => {
        setServer(r);
        setFamilies(r.families.map((f) => ({...f, genres: [...f.genres]})));
        setCatchAll([...r.catch_all]);
    };
    useEffect(() => { getGenres().then(load).catch((e) => setError(e.message)); }, []);

    const counts = useMemo(() => Object.fromEntries((server?.catalog || []).map((c) => [c.genre, c.songs])), [server]);
    const owner = useMemo(() => {
        const m = {};
        families.forEach((f) => f.genres.forEach((g) => { m[g] = f.key; }));
        catchAll.forEach((g) => { m[g] = CATCH_ALL; });
        return m;
    }, [families, catchAll]);
    const unassigned = (server?.catalog || []).filter((c) => !owner[c.genre]).map((c) => c.genre);
    const songsIn = (genres) => genres.reduce((acc, g) => acc + (counts[g] || 0), 0);
    const dirty = server && JSON.stringify({f: families, c: catchAll})
        !== JSON.stringify({f: server.families, c: server.catch_all});

    const labelOf = (key) => (key === CATCH_ALL ? 'Catch-all' : key === UNASSIGNED ? 'Unassigned'
        : families.find((f) => f.key === key)?.label || key);

    /** Move a genre to a family, the catch-all list, or unassigned. */
    const move = (genre, to) => {
        setFamilies((fs) => fs.map((f) => ({
            ...f,
            genres: f.key === to ? [...f.genres.filter((g) => g !== genre), genre] : f.genres.filter((g) => g !== genre),
        })));
        setCatchAll((c) => (to === CATCH_ALL ? [...c.filter((g) => g !== genre), genre] : c.filter((g) => g !== genre)));
    };

    const addFamily = () => {
        const label = window.prompt('Name of the new genre family');
        if (!label) return;
        let key = slug(label);
        while (families.some((f) => f.key === key)) key += '2';
        setFamilies((fs) => [...fs, {key, label, genres: []}]);
        setSelected(key);
    };
    const renameFamily = (key, label) => setFamilies((fs) => fs.map((f) => (f.key === key ? {...f, label} : f)));
    const deleteFamily = (key) => {
        if (!window.confirm(`Delete ${labelOf(key)}? Its genres become unassigned.`)) return;
        setFamilies((fs) => fs.filter((f) => f.key !== key));
        setSelected(UNASSIGNED);
    };

    const run = (promise, ok) => {
        setBusy(true);
        setError(null);
        promise.then((r) => { load(r); setNotice(ok); }).catch((e) => setError(e.message)).finally(() => setBusy(false));
    };

    if (!server) {
        return <p className="text-white/40">{error || 'Loading genres…'}</p>;
    }

    const groupGenres = selected === CATCH_ALL ? catchAll
        : selected === UNASSIGNED ? unassigned
            : families.find((f) => f.key === selected)?.genres || [];
    const shown = [...groupGenres]
        .filter((g) => g.toLowerCase().includes(filter.toLowerCase()))
        .sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
    const addable = selected === UNASSIGNED ? [] : (server.catalog || [])
        .map((c) => c.genre)
        .filter((g) => owner[g] !== selected && filter && g.toLowerCase().includes(filter.toLowerCase()))
        .slice(0, 12);
    const family = families.find((f) => f.key === selected);

    const GroupButton = ({id, label, genres, sub}) => (
        <button type="button" onClick={() => { setSelected(id); setFilter(''); }}
                className={'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-[12.5px] transition '
                    + (selected === id ? 'bg-white/[0.10] text-white' : 'text-white/60 hover:bg-white/[0.05] hover:text-white')}>
            <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{label}</span>
                {sub && <span className="block text-[10.5px] text-white/35">{sub}</span>}
            </span>
            <span className="text-right text-[10.5px] tabular-nums text-white/40">
                {genres.length} · {n(songsIn(genres))}
            </span>
        </button>
    );

    return (
        <div className="max-w-[1100px]">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="font-heading text-[28px] font-bold tracking-[-0.015em]">Genres</h1>
                    <p className="mt-1 max-w-[640px] text-[13px] text-white/45">
                        Genre names come from Apple. Families group them: songs in the same family earn the family bonus, and each family can
                        have its own scoring profile. Catch-all genres (soundtracks, holiday…) use the artist's main genre instead.
                    </p>
                </div>
                <div className="flex gap-2">
                    {dirty && <span className="self-center rounded-full bg-amber-400/15 px-2 py-0.5 text-[9.5px] font-extrabold uppercase text-amber-300">Unsaved</span>}
                    <button type="button" disabled={!dirty || busy} onClick={() => load(server)}
                            className="rounded-xl bg-white/[0.08] px-4 py-2 text-[12.5px] font-semibold text-white/70 hover:text-white disabled:opacity-30">
                        Revert
                    </button>
                    <button type="button" disabled={!dirty || busy}
                            onClick={() => window.confirm('Save the genre families? Recommendations use them within a minute.')
                                && run(saveGenres({families, catch_all: catchAll}), 'Saved. Recommendations use the new families within a minute.')}
                            className="rounded-xl bg-apple-red px-4 py-2 text-[12.5px] font-bold disabled:opacity-30">
                        Save genres
                    </button>
                </div>
            </div>

            {error && <p className="mt-4 text-[13px] text-apple-red">{error}</p>}
            {notice && !error && <p className="mt-4 text-[13px] text-emerald-300">{notice}</p>}

            <div className="mt-6 grid gap-6 md:grid-cols-[280px_1fr]">
                <div className="space-y-1 rounded-2xl bg-white/[0.04] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                    <GroupButton id={UNASSIGNED} label="Unassigned" genres={unassigned} sub="No family: exact genre matches only"/>
                    <GroupButton id={CATCH_ALL} label="Catch-all" genres={catchAll} sub="Uses the artist's main genre"/>
                    <div className="my-1 h-px bg-white/[0.06]"/>
                    {families.map((f) => <GroupButton key={f.key} id={f.key} label={f.label} genres={f.genres}/>)}
                    <button type="button" onClick={addFamily}
                            className="mt-1 w-full rounded-xl px-3 py-2 text-left text-[12.5px] font-semibold text-white/50 hover:bg-white/[0.05] hover:text-white">
                        <i className="fa-solid fa-plus mr-2 text-[10px]"/>New family
                    </button>
                    <p className="px-3 pb-1 pt-2 text-[10.5px] text-white/35">Genres · songs</p>
                    {server.saved && (
                        <button type="button" disabled={busy}
                                onClick={() => window.confirm('Drop the saved genre families and go back to the file defaults?')
                                    && run(resetGenres(), 'Back to the file defaults.')}
                                className="w-full rounded-xl px-3 py-2 text-left text-[11.5px] text-white/40 hover:text-white">
                            Back to file defaults
                        </button>
                    )}
                </div>

                <div className="rounded-2xl bg-white/[0.04] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                    <div className="flex flex-wrap items-center gap-3">
                        {family ? (
                            <input value={family.label} onChange={(e) => renameFamily(family.key, e.target.value)}
                                   className="rounded-lg bg-transparent px-1 font-heading text-[20px] font-bold outline-none focus:bg-white/[0.06]"/>
                        ) : (
                            <h2 className="font-heading text-[20px] font-bold">{labelOf(selected)}</h2>
                        )}
                        <span className="text-[12px] text-white/40">{groupGenres.length} genres · {n(songsIn(groupGenres))} songs</span>
                        {family && (
                            <button type="button" onClick={() => deleteFamily(family.key)}
                                    className="ml-auto text-[11.5px] text-white/40 hover:text-apple-red">Delete family</button>
                        )}
                    </div>

                    <div className="mt-4 flex items-center gap-3 rounded-xl bg-white/[0.07] px-3.5 py-2.5">
                        <i className="fa-solid fa-magnifying-glass text-[12px] text-white/40"/>
                        <input value={filter} onChange={(e) => setFilter(e.target.value)}
                               placeholder={selected === UNASSIGNED ? 'Filter genres…' : 'Filter, or type to find genres to add…'}
                               className="w-full bg-transparent text-[13px] text-white placeholder-white/30 outline-none"/>
                    </div>

                    {addable.length > 0 && (
                        <div className="mt-3 rounded-xl bg-white/[0.04] p-2">
                            <div className="px-1 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-white/35">Add to {labelOf(selected)}</div>
                            {addable.map((g) => (
                                <button key={g} type="button" onClick={() => move(g, selected)}
                                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] hover:bg-white/[0.06]">
                                    <i className="fa-solid fa-plus text-[9px] text-white/40"/>
                                    <span className="flex-1">{g}</span>
                                    <span className="text-[10.5px] text-white/40">{owner[g] ? 'from ' + labelOf(owner[g]) : 'unassigned'} · {n(counts[g] || 0)}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="scrollbar-subtle mt-4 max-h-[62vh] overflow-y-auto pr-1">
                        {shown.length === 0 && <p className="text-[12.5px] text-white/40">No genres here{filter ? ' match' : ''}.</p>}
                        {selected === UNASSIGNED ? (
                            <div className="divide-y divide-white/[0.05]">
                                {shown.map((g) => (
                                    <div key={g} className="flex items-center gap-3 py-1.5 text-[12.5px]">
                                        <span className="flex-1">{g}</span>
                                        <span className="w-16 text-right text-[11px] tabular-nums text-white/40">{n(counts[g] || 0)}</span>
                                        <select value="" onChange={(e) => e.target.value && move(g, e.target.value)}
                                                className="rounded-lg bg-white/[0.07] px-2 py-1 text-[11.5px] text-white/70 outline-none">
                                            <option value="">Assign to…</option>
                                            {families.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                                            <option value={CATCH_ALL}>Catch-all</option>
                                        </select>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {shown.map((g) => (
                                    <span key={g} className="flex items-center gap-2 rounded-full bg-white/[0.08] py-1 pl-3 pr-1.5 text-[12px]">
                                        {g}
                                        <span className="text-[10.5px] tabular-nums text-white/40">{n(counts[g] || 0)}</span>
                                        <button type="button" onClick={() => move(g, UNASSIGNED)} title="Remove from this group"
                                                className="flex h-5 w-5 items-center justify-center rounded-full text-white/40 hover:bg-white/10 hover:text-white">
                                            <i className="fa-solid fa-xmark text-[9px]"/>
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
